import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  useGetWebhooksQuery,
  useCreateWebhookMutation,
  useUpdateWebhookMutation,
  useDeleteWebhookMutation,
  useTestWebhookMutation,
  useToggleWebhookStatusMutation,
  useGetWebhookEventTypesQuery,
  useGetWebhookStatsQuery,
} from "../store/api";
import {
  Webhook,
  CreateWebhookRequest,
  UpdateWebhookRequest,
  WebhookFilters,
} from "../types/webhook";
import WebhookTable from "../components/webhooks/WebhookTable";
import WebhookModal from "../components/webhooks/WebhookModal";
import WebhookStats from "../components/webhooks/WebhookStats";
import WebhookFiltersComponent from "../components/webhooks/WebhookFilters";
import WebhookLogsModal from "../components/webhooks/WebhookLogsModal";
import { useNotification } from "../hooks/useNotification";

const WebhooksPage: React.FC = () => {
  const { t } = useTranslation(["webhooks", "common"]);
  const { showNotification } = useNotification();
  const navigate = useNavigate();

  const [filters, setFilters] = useState<WebhookFilters>({
    event_type: "",
    is_active: undefined,
    search: "",
    sort_by: "created_at",
    sort_order: "desc",
    per_page: 15,
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingWebhook, setEditingWebhook] = useState<Webhook | null>(null);
  const [isLogsModalOpen, setIsLogsModalOpen] = useState(false);
  const [selectedWebhookForLogs, setSelectedWebhookForLogs] = useState<{
    id: number;
    name: string;
  } | null>(null);

  // API queries
  const {
    data: webhooksData,
    isLoading,
    error,
    refetch,
  } = useGetWebhooksQuery(filters);
  const { data: eventTypesData } = useGetWebhookEventTypesQuery();
  const { data: statsData } = useGetWebhookStatsQuery();

  // Mutations
  const [createWebhook, { isLoading: isCreating }] = useCreateWebhookMutation();
  const [updateWebhook, { isLoading: isUpdating }] = useUpdateWebhookMutation();
  const [deleteWebhook, { isLoading: isDeleting }] = useDeleteWebhookMutation();
  const [testWebhook, { isLoading: isTesting }] = useTestWebhookMutation();
  const [toggleStatus, { isLoading: isToggling }] =
    useToggleWebhookStatusMutation();

  const handleCreateWebhook = async (
    data: CreateWebhookRequest | UpdateWebhookRequest
  ) => {
    try {
      await createWebhook(data as CreateWebhookRequest).unwrap();
      showNotification(t("webhooks:messages.webhook_created"), "success");
      setIsModalOpen(false);
      refetch();
    } catch (error: any) {
      showNotification(
        error.data?.message || t("webhooks:errors.error_creating_webhook"),
        "error"
      );
    }
  };

  const handleUpdateWebhook = async (data: UpdateWebhookRequest) => {
    if (!editingWebhook) return;

    try {
      await updateWebhook({ id: editingWebhook.id, data }).unwrap();
      showNotification(t("webhooks:messages.webhook_updated"), "success");
      setIsModalOpen(false);
      setEditingWebhook(null);
      refetch();
    } catch (error: any) {
      showNotification(
        error.data?.message || t("webhooks:errors.error_updating_webhook"),
        "error"
      );
    }
  };

  const handleDeleteWebhook = async (id: number) => {
    if (!window.confirm(t("webhooks:messages.confirm_delete_webhook"))) return;

    try {
      await deleteWebhook(id).unwrap();
      showNotification(t("webhooks:messages.webhook_deleted"), "success");
      refetch();
    } catch (error: any) {
      showNotification(
        error.data?.message || t("webhooks:errors.error_deleting_webhook"),
        "error"
      );
    }
  };

  const handleTestWebhook = async (id: number) => {
    try {
      await testWebhook(id).unwrap();
      showNotification(t("webhooks:messages.webhook_tested"), "success");
    } catch (error: any) {
      showNotification(
        error.data?.message || t("webhooks:errors.error_testing_webhook"),
        "error"
      );
    }
  };

  const handleToggleStatus = async (id: number) => {
    try {
      await toggleStatus(id).unwrap();
      showNotification(t("webhooks:messages.status_toggled"), "success");
      refetch();
    } catch (error: any) {
      showNotification(
        error.data?.message || t("webhooks:errors.error_toggling_status"),
        "error"
      );
    }
  };

  const handleViewEndpoints = (webhookId: number) => {
    // Переходим на страницу эндпойнтов с фильтром по webhook_id
    navigate(`/dashboard/webhook-endpoints?webhook_id=${webhookId}`);
  };

  const handleViewLogs = (webhookId: number, webhookName: string) => {
    setSelectedWebhookForLogs({ id: webhookId, name: webhookName });
    setIsLogsModalOpen(true);
  };

  const handleEditWebhook = (webhook: Webhook) => {
    setEditingWebhook(webhook);
    setIsModalOpen(true);
  };

  const handleAddWebhook = () => {
    setEditingWebhook(null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingWebhook(null);
  };

  const handleCloseLogsModal = () => {
    setIsLogsModalOpen(false);
    setSelectedWebhookForLogs(null);
  };

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-800">
            {t("webhooks:errors.error_loading_webhooks")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          {t("webhooks:title")}
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          {t("webhooks:webhooks")} - {t("webhooks:description")}
        </p>
      </div>

      {/* Statistics */}
      {statsData?.data && <WebhookStats stats={statsData.data} />}

      {/* Filters */}
      <WebhookFiltersComponent
        filters={filters}
        onFiltersChange={setFilters}
        eventTypes={eventTypesData?.data || {}}
      />

      {/* Actions */}
      <div className="mb-4 flex justify-between items-center">
        <div className="flex space-x-2">
          <button
            onClick={handleAddWebhook}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md transition-colors"
          >
            {t("webhooks:add_webhook")}
          </button>
        </div>
      </div>

      {/* Table */}
      <WebhookTable
        webhooks={webhooksData?.data || []}
        isLoading={isLoading}
        onEdit={handleEditWebhook}
        onDelete={handleDeleteWebhook}
        onTest={handleTestWebhook}
        onToggleStatus={handleToggleStatus}
        onViewEndpoints={handleViewEndpoints}
        onViewLogs={handleViewLogs}
        isDeleting={isDeleting}
        isTesting={isTesting}
        isToggling={isToggling}
      />

      {/* Webhook Modal */}
      {isModalOpen && (
        <WebhookModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          webhook={editingWebhook}
          eventTypes={eventTypesData?.data || {}}
          onSubmit={editingWebhook ? handleUpdateWebhook : handleCreateWebhook}
          isLoading={isCreating || isUpdating}
        />
      )}

      {/* Logs Modal */}
      {isLogsModalOpen && selectedWebhookForLogs && (
        <WebhookLogsModal
          isOpen={isLogsModalOpen}
          onClose={handleCloseLogsModal}
          webhookId={selectedWebhookForLogs.id}
          webhookName={selectedWebhookForLogs.name}
        />
      )}
    </div>
  );
};

export default WebhooksPage;
